import { useState } from 'react';
import type { AuditNote } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, MessageSquare } from 'lucide-react';

interface AuditNotesProps {
  notes: AuditNote[];
  canAdd: boolean;
  onAdd: (body: string) => Promise<boolean>;
}

/** Follow-up notes: append-only trail, newest first (action status changes add their own). */
export default function AuditNotes({ notes, canAdd, onAdd }: AuditNotesProps) {
  const { formatDateTime } = useFormatters();
  const [body, setBody] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const submit = async () => {
    if (!body.trim()) return;
    setSaving(true);
    const ok = await onAdd(body.trim());
    setSaving(false);
    if (ok) setBody('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-5 w-5" />Follow-up notes ({notes.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canAdd && (
          <div className="space-y-2">
            <Textarea value={body} rows={3} className="text-base" onChange={(e) => setBody(e.target.value)}
              placeholder="Add a follow-up note" aria-label="New follow-up note" />
            <Button onClick={submit} disabled={!body.trim() || saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add note
            </Button>
          </div>
        )}
        {notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => (
              <li key={n.id} className="border-l-4 border-blue-500 pl-4 py-1">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium">{n.author_name || 'System'}</p>
                  <p className="text-xs text-muted-foreground">{formatDateTime(n.created_at)}</p>
                </div>
                <p className="text-base whitespace-pre-wrap">{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
